/**
 * Service de tracking d'erreurs centralisé (Sentry)
 *
 * Initialise Sentry pour capturer les erreurs en production.
 * Fonctionne silencieusement si VITE_SENTRY_DSN n'est pas configuré.
 *
 * Sentry est chargé dynamiquement pour ne pas bloquer le first paint.
 * Les captures avant initialisation sont silencieusement ignorées.
 */
type SentryModule = typeof import('@sentry/react');

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT as string || 'development';
const IS_PRODUCTION = ENVIRONMENT === 'PRODUCTION';

let initialized = false;
let sentryRef: SentryModule | null = null;

async function loadAndInit(): Promise<void> {
  if (initialized) return;
  initialized = true;

  try {
    const raw = localStorage.getItem('fitd-cookie-consent');
    if (raw) {
      const stored = JSON.parse(raw);
      if (stored?.consent?.errorTracking === false) {
        return;
      }
    }
  } catch { /* proceed if localStorage fails */ }

  if (!SENTRY_DSN) {
    if (!IS_PRODUCTION) {
      console.info('[ErrorTracking] Sentry DSN non configuré — tracking désactivé.');
    }
    return;
  }

  const Sentry = await import('@sentry/react');
  sentryRef = Sentry;

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT.toLowerCase(),
    sampleRate: 1.0,
    tracesSampleRate: IS_PRODUCTION ? 0.2 : 1.0,
    ignoreErrors: [
      'ResizeObserver loop',
      'Non-Error promise rejection captured',
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
    ],
    beforeSend(event) {
      if (!SENTRY_DSN) return null;
      return event;
    },
  });
}

/**
 * Initialise Sentry. À appeler une seule fois au démarrage de l'app (index.tsx).
 * L'import Sentry est déféré hors du chemin critique du first paint via
 * requestIdleCallback (fallback setTimeout).
 */
export function initErrorTracking(): void {
  if (initialized) return;

  const schedule = (cb: () => void) => {
    if (typeof window === 'undefined') return;
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void }).requestIdleCallback;
    if (typeof ric === 'function') {
      ric(() => { void cb(); }, { timeout: 2000 });
    } else {
      setTimeout(() => { void cb(); }, 0);
    }
  };

  schedule(() => { void loadAndInit(); });
}

export function captureError(
  error: unknown,
  context?: Record<string, unknown>
): void {
  console.error('[ErrorTracking]', error);

  if (!sentryRef || !SENTRY_DSN) return;

  if (error instanceof Error) {
    sentryRef.captureException(error, { extra: context });
  } else {
    sentryRef.captureMessage(String(error), { level: 'error', extra: context });
  }
}

export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info',
  context?: Record<string, unknown>
): void {
  if (!sentryRef || !SENTRY_DSN) return;
  sentryRef.captureMessage(message, { level, extra: context });
}

export function setUser(user: { id: string; email?: string } | null): void {
  if (!sentryRef || !SENTRY_DSN) return;

  if (user) {
    sentryRef.setUser({ id: user.id, email: user.email });
  } else {
    sentryRef.setUser(null);
  }
}
